<template>
  <div>
    <span class="text-caption grey--text text--lighten-1">
      This application is not intended to be used as a medical device by the
      manufacturer.
    </span>
    <v-container class="d-flex align-center pa-0">
      <div class="my-3 ml-3 mr-13">
        <v-fade-transition>
          <div
            v-if="!transition && validationPass"
            class="d-flex flex-column align-center"
          >
            <v-progress-circular
              :color="validationReconsider ? 'warning' : 'success'"
              size="96"
              :rotate="270"
              :value="progress"
              class="progress-circular-transition"
            >
              <span
                v-if="!validationReconsider"
                class="success--text title-font"
                style="font-size: x-large"
              >
                PASS
              </span>
              <span
                v-if="validationReconsider"
                class="warning--text title-font"
                style="font-size: x-large"
              >
                WARN
              </span>
            </v-progress-circular>
          </div>
        </v-fade-transition>
        <v-fade-transition>
          <div
            v-if="!transition && !validationPass"
            class="d-flex flex-column align-center"
          >
            <v-progress-circular
              color="error"
              size="96"
              :rotate="270"
              :value="progress"
              class="progress-circular-transition"
            >
              <span class="error--text title-font" style="font-size: x-large">
                FAIL
              </span>
            </v-progress-circular>
          </div>
        </v-fade-transition>
      </div>
      <div>
        <v-row align="center" class="mt-0">
          <v-btn
            @click="onScanColorsButtonClick"
            :color="
              validationReconsider
                ? 'warning'
                : validationPass
                ? 'success'
                : 'error'
            "
            class="text-body-2 elevation-0 mt-5 mr-8 rounded-10"
          >
            {{ toggleScanColorsButtonLabel }}
          </v-btn>
          <v-checkbox
            v-model="showScanTissue"
            label="Show Scan Tissue"
            hide-details
          />
        </v-row>
        <v-row :style="validationReconsider ? null : 'height: 90px'">
          <v-col v-if="showHeatmapColors">
            <HeatmapScale class="ml-5" />
          </v-col>
        </v-row>
        <v-row v-if="validationReconsider">
          <v-col class="pb-0 ps-0">
            <v-card-text class="text-left ps-0 pb-0">
              <h3 class="black--text mb-4">Consider Rescanning</h3>
              <p>
                The Scan Validation process has determined that the scans
                provided may produce inaccurate results. This is a warning only,
                the case may continue at your discretion.
              </p>
            </v-card-text>
          </v-col>
        </v-row>
      </div>
    </v-container>
    <v-row>
      <v-col cols="12" class="text-center mt-5" v-if="analyzerSession">
        <span>Your session reference number is:</span>
        <h1 class="mt-5">
          {{ analyzerSession.reference_number }}
        </h1>
        <p class="mt-5" v-if="analyzerSession.session_type">
          ({{ analyzerSession.session_type }})
        </p>
      </v-col>
    </v-row>
    <div
      v-if="uploadedScans.length === 3 && validationPass === false"
      class="mt-5 mb-n2"
    >
      &nbsp;<v-icon color="error" class="mr-1">mdi-alert-circle</v-icon>
      Maximum retry count reached.
    </div>
    <v-row class="mt-0">
      <v-col>
        <template v-if="!validationPass">
          <v-btn
            :disabled="!retryPermitted"
            @click="onRetry"
            class="elevation-0 rounded-10 my-2 me-2"
          >
            Retry With New Scans
          </v-btn>
          <v-btn class="elevation-0 rounded-10 mr-2" @click="restart">
            Restart Session
          </v-btn>
        </template>
        <template v-if="validationPass">
          <v-btn class="elevation-0 rounded-10 my-1 me-2" @click="restart">
            Restart Session
          </v-btn>
          <v-btn
            v-if="isEmployee"
            class="btn-primary mr-2 my-1"
            @click="$emit('next')"
          >
            Continue to Library Association
          </v-btn>
        </template>
        <v-btn
          v-if="isEmployee && validatedScanFiles"
          class="btn-primary my-1"
          @click="downloadScanFiles()"
        >
          <v-icon class="mr-2">mdi-download</v-icon>Download Scan Files
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<style>
.progress-circular-transition .v-progress-circular__overlay {
  transition: all 0.6s ease-in-out !important;
}
</style>

<script>
import { mapActions, mapGetters } from "vuex"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import JSZip from "jszip"
import HeatmapScale from "./PostAlignment/HeatmapScale.vue"
import heatmapImg from "@/assets/image-heatmap-scale-light.svg"

export default {
  name: "PostAlignment",
  components: {
    HeatmapScale
  },

  data() {
    return {
      heatmapImg,
      progress: 0,
      showHeatmapColors: false,
      showScanTissue: true
    }
  },

  async mounted() {
    setTimeout(() => (this.progress = 100), 500)
  },

  watch: {
    showScanTissue(value) {
      NCFrontend.get("scanValidationView").visualisation.setScanTissueVisible(
        value
      )
    }
  },

  computed: {
    ...mapGetters([
      "analyzerSession",
      "validateScansResponse",
      "validationPass",
      "validationReconsider",
      "uploadedScans",
      "transition",
      "retryCount",
      "isEmployee",
      "validatedScanFiles"
    ]),

    toggleScanColorsButtonLabel() {
      if (!this.showHeatmapColors) {
        return "Show Heatmap Colors"
      }
      return "Show Score Colors"
    },

    retryPermitted() {
      return this.uploadedScans.length < 3
    }
  },
  methods: {
    ...mapActions([
      "setValidateScansResponse",
      "setRetryCount",
      "setPageState",
      "setScanFiles",
      "setRetrying",
      "restartSession",
      "setAnalyzerSession"
    ]),

    onScanColorsButtonClick() {
      if (!this.showHeatmapColors) {
        NCFrontend.get("scanValidationView").visualisation.showHeatmapColors()
        this.showHeatmapColors = true
      } else {
        NCFrontend.get("scanValidationView").visualisation.showScoreColors()
        this.showHeatmapColors = false
      }
    },

    restart() {
      if (NCFrontend.get("scan1View"))
        NCFrontend.get("scan1View").visualisation.reset()

      if (NCFrontend.get("scan2View"))
        NCFrontend.get("scan2View").visualisation.reset()

      NCFrontend.remove(
        NCFrontend.get("scan1View"),
        NCFrontend.get("scan2View")
      )

      this.restartSession()
      this.$emit("switch")
      this.$emit("step", "CaseType")
    },

    onRetry() {
      if (NCFrontend.get("scan1View"))
        NCFrontend.get("scan1View").clearVisualisation()
      if (NCFrontend.get("scan2View"))
        NCFrontend.get("scan2View").clearVisualisation()
      NCFrontend.get("scanValidationView").clearVisualisation()
      this.setValidateScansResponse(null)
      this.setScanFiles([])
      this.setRetryCount(this.retryCount + 1)
      this.setRetrying(true)

      this.setPageState([{ cols: 7, component: "ConnectBranding" }])
      this.$emit("switch")

      this.$emit("step", "UploadScans")
    },

    async downloadScanFiles() {
      const zip = new JSZip()
      for (const file of this.validatedScanFiles) {
        try {
          const response = await fetch(file.url)

          const blob = await response.blob()
          zip.file(file.oftFileName, blob)
        } catch (err) {
          console.error(`An internal error occurred.`)
          throw err
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" })

      const url = URL.createObjectURL(zipBlob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${this.analyzerSession.reference_number}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }
}
</script>
